import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/tiptap/styles.css";

import {
  createTheme,
  MantineProvider,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Provider } from "react-redux";
import { store } from "./store.ts";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Notifications } from "@mantine/notifications";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AuthProvider } from "./authentication/AuthProvider.tsx";
import { UserAttributesHack } from "./authentication/UserAttributesHack.tsx";
import { Footer } from "./components/Footer.tsx";

dayjs.extend(customParseFormat);

// Extend the Window interface to include recaptchaSiteKey and googleClientId
declare global {
  interface Window {
    recaptchaSiteKey: string;
    googleClientId: string;
  }
}
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const theme = createTheme({
  components: {
    TextInput: TextInput.extend({
      defaultProps: {
        width: "150px",
      },
    }),
  },
});

// Dynamically load the reCAPTCHA script
// In local development using vite we don't supply the recaptchaSiteKey, so we use the site key used for testing (associated with earlybirdrsvp-dev)
// When using the webserver, we set the recaptchaSiteKey in index.html
if (window.recaptchaSiteKey === "RECAPTCHA_SITE_KEY")
  window.recaptchaSiteKey = "6LfAflsrAAAAALLCahwTtJZMlZnNSsoOwdKBmU6g";
const script = document.createElement("script");
script.src = `https://www.google.com/recaptcha/api.js?render=${window.recaptchaSiteKey}`;
script.async = true;
script.defer = true;
document.head.appendChild(script);

// The GOOGLE_CLIENT_ID (used for signin with Google) is set in the index.html file for production builds
// For local development using vite, we set it here to the one used for testing (associated with earlybirdrsvp-dev)
if (window.googleClientId === "GOOGLE_CLIENT_ID")
  window.googleClientId =
    "801631728689-07u70vlp9mncmo787aqjtrocs66ieb61.apps.googleusercontent.com";

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MantineProvider defaultColorScheme="dark" theme={theme}>
      <Tooltip.Group openDelay={500} closeDelay={100}>
        <ModalsProvider>
          <AuthProvider>
            <Notifications />
            <Provider store={store}>
              <UserAttributesHack>
                <App />
                <Footer />
              </UserAttributesHack>
            </Provider>
          </AuthProvider>
        </ModalsProvider>
      </Tooltip.Group>
    </MantineProvider>
  </StrictMode>
);
